// The Notes facet of the /transcripts/$id Dossier (transcript-timeline.md):
// the keyset-paginated note list first, then the create composer BELOW it —
// create never renders above primary content (UI-LAW §4b.2). CRUD is whole:
// draft composer, inline edit with Save/Cancel (§2.12 action row), Delete
// behind the inline danger-confirm reveal with the descriptive sentence.

import { useMemo, useState, type SyntheticEvent } from "react";

import type { ColumnDef } from "@tanstack/react-table";

import type { components } from "../api/schema";
import { formatDateTime, formatLocator } from "../lib/format";
import type { ListQueryResult } from "../lib/useListQuery";
import {
  Button,
  EmptyState,
  Inline,
  InlineError,
  Markdown,
  Stack,
  Text,
  Textarea,
} from "../ui";
import { PaginatedTableSection } from "./shared/PaginatedTableSection";
import { userDisplayName, UserLabel } from "./shared/userDirectory";

type NoteResponse = components["schemas"]["NoteResponse"];
type UserResponse = components["schemas"]["UserResponse"];

const EMPTY_USERS: ReadonlyMap<string, UserResponse> = new Map();

export interface TranscriptNotesProps {
  editingNoteBody: string;
  editingNoteId: string | null;
  isCreatingNote?: boolean;
  isDeletingNote?: boolean;
  isSavingNote?: boolean;
  mutationError?: string | null;
  noteDraft: string;
  notes: ListQueryResult<NoteResponse>;
  onCancelEditNote: () => void;
  onCreateNote: () => void;
  onDeleteNote: (note: NoteResponse) => void;
  onEditNoteBodyChange: (body: string) => void;
  onNoteDraftChange: (body: string) => void;
  onSaveNote: (note: NoteResponse) => void;
  onStartEditNote: (note: NoteResponse) => void;
  usersById?: ReadonlyMap<string, UserResponse>;
}

export function TranscriptNotes({
  editingNoteBody,
  editingNoteId,
  isCreatingNote = false,
  isDeletingNote = false,
  isSavingNote = false,
  mutationError = null,
  noteDraft,
  notes,
  onCancelEditNote,
  onCreateNote,
  onDeleteNote,
  onEditNoteBodyChange,
  onNoteDraftChange,
  onSaveNote,
  onStartEditNote,
  usersById = EMPTY_USERS,
}: TranscriptNotesProps) {
  const [expandedNoteId, setExpandedNoteId] = useState<string | null>(null);
  const activeExpandedNoteId = notes.items.some(
    (note) => note.id === (editingNoteId ?? expandedNoteId),
  )
    ? (editingNoteId ?? expandedNoteId)
    : null;
  const columns = useMemo<Array<ColumnDef<NoteResponse>>>(
    () =>
      noteColumns({
        expandedNoteId: activeExpandedNoteId,
        onToggleNote: (noteId) => {
          if (activeExpandedNoteId === noteId) {
            if (editingNoteId === noteId) {
              onCancelEditNote();
            }
            setExpandedNoteId(null);
            return;
          }
          setExpandedNoteId(noteId);
        },
        usersById,
      }),
    [activeExpandedNoteId, editingNoteId, onCancelEditNote, usersById],
  );

  return (
    <Stack gap={4}>
      <PaginatedTableSection
        ariaLabel="Notes"
        boundedColumns={["note"]}
        columns={columns}
        emptyState={<EmptyState title="No notes" />}
        errorMessage={notes.errorMessage}
        getRowId={(note) => note.id}
        hasNextPage={notes.hasNextPage}
        isFetchingNextPage={notes.isFetchingNextPage}
        isPending={notes.isPending}
        items={notes.items}
        listStateKey={notes.listStateKey}
        onLoadMore={notes.fetchNextPage}
        isRowExpanded={(row) => row.original.id === activeExpandedNoteId}
        renderExpandedRow={(row) => (
          <NoteRecord
            editingBody={editingNoteBody}
            editingNoteId={editingNoteId}
            isDeleting={isDeletingNote}
            isSaving={isSavingNote}
            note={row.original}
            onCancelEditNote={onCancelEditNote}
            onDeleteNote={onDeleteNote}
            onEditBodyChange={onEditNoteBodyChange}
            onSaveNote={onSaveNote}
            onStartEditNote={onStartEditNote}
          />
        )}
        skeletonLabel="Loading notes"
        virtual={
          activeExpandedNoteId === null
            ? { interactiveRows: true, maxHeight: 600 }
            : undefined
        }
      />
      {mutationError ? <InlineError>{mutationError}</InlineError> : null}
      <form
        onSubmit={(event: SyntheticEvent<HTMLFormElement>) => {
          event.preventDefault();
          onCreateNote();
        }}
      >
        <Stack gap={3}>
          <Textarea
            label="Note body"
            value={noteDraft}
            onChange={(event) => {
              onNoteDraftChange(event.target.value);
            }}
          />
          <Inline>
            <Button
              disabled={isCreatingNote || noteDraft.trim().length === 0}
              type="submit"
            >
              {isCreatingNote ? "Adding..." : "Add Note"}
            </Button>
          </Inline>
        </Stack>
      </form>
    </Stack>
  );
}

function noteColumns({
  expandedNoteId,
  onToggleNote,
  usersById,
}: {
  expandedNoteId: string | null;
  onToggleNote: (noteId: string) => void;
  usersById: ReadonlyMap<string, UserResponse>;
}): Array<ColumnDef<NoteResponse>> {
  return [
    {
      header: "Note",
      id: "note",
      cell: ({ row }) => (
        <Inline className="marker-note-row" gap={2}>
          <Text as="span" title={row.original.body} truncate>
            {row.original.body}
          </Text>
          <Button
            aria-expanded={expandedNoteId === row.original.id}
            onClick={() => {
              onToggleNote(row.original.id);
            }}
            size="sm"
            variant="ghost"
          >
            {expandedNoteId === row.original.id ? "Close" : "View"}
          </Button>
        </Inline>
      ),
    },
    {
      header: "Author",
      cell: ({ row }) => {
        const authorId = row.original.author_id;
        return authorId == null ? (
          <Text as="span" variant="muted">
            —
          </Text>
        ) : (
          <UserLabel
            compact
            fallbackId={authorId}
            user={usersById.get(authorId)}
          />
        );
      },
    },
    {
      header: "Created",
      cell: ({ row }) => formatDateTime(row.original.created_at),
    },
    {
      header: "Edited",
      cell: ({ row }) => {
        const editorId = row.original.edited_by;
        if (editorId == null) {
          return "—";
        }
        const editor = usersById.get(editorId);
        if (editor == null) {
          return (
            <Inline gap={1}>
              <Text as="span" variant="muted">
                Edited by
              </Text>
              <UserLabel compact fallbackId={editorId} />
              <Text as="span" variant="muted">
                {formatDateTime(row.original.updated_at)}
              </Text>
            </Inline>
          );
        }
        return `Edited by ${userDisplayName(editor)} ${formatDateTime(
          row.original.updated_at,
        )}`;
      },
    },
    {
      header: "Locator",
      cell: ({ row }) => formatLocator(row.original.locator),
    },
  ];
}

function NoteRecord({
  editingBody,
  editingNoteId,
  isDeleting,
  isSaving,
  note,
  onCancelEditNote,
  onDeleteNote,
  onEditBodyChange,
  onSaveNote,
  onStartEditNote,
}: {
  editingBody: string;
  editingNoteId: string | null;
  isDeleting: boolean;
  isSaving: boolean;
  note: NoteResponse;
  onCancelEditNote: () => void;
  onDeleteNote: (note: NoteResponse) => void;
  onEditBodyChange: (body: string) => void;
  onSaveNote: (note: NoteResponse) => void;
  onStartEditNote: (note: NoteResponse) => void;
}) {
  const isEditing = editingNoteId === note.id;
  const [isConfirmingDelete, setIsConfirmingDelete] = useState(false);

  return (
    <Stack gap={2}>
      {isEditing ? (
        <form
          onSubmit={(event) => {
            event.preventDefault();
            onSaveNote(note);
          }}
        >
          <Stack gap={3}>
            <Textarea
              label="Edit note body"
              value={editingBody}
              onChange={(event) => {
                onEditBodyChange(event.target.value);
              }}
            />
            <Inline gap={2}>
              <Button
                disabled={isSaving || editingBody.trim().length === 0}
                size="sm"
                type="submit"
                variant="primary"
              >
                {isSaving ? "Saving..." : "Save"}
              </Button>
              <Button
                disabled={isSaving}
                size="sm"
                type="button"
                variant="secondary"
                onClick={onCancelEditNote}
              >
                Cancel
              </Button>
            </Inline>
          </Stack>
        </form>
      ) : (
        <>
          <Inline gap={2}>
            <Button
              size="sm"
              variant="secondary"
              onClick={() => {
                onStartEditNote(note);
              }}
            >
              Edit
            </Button>
            <Button
              disabled={isDeleting}
              size="sm"
              variant="danger"
              onClick={() => {
                setIsConfirmingDelete(true);
              }}
            >
              Delete
            </Button>
          </Inline>
          <Markdown>{note.body}</Markdown>
          {isConfirmingDelete ? (
            <Stack gap={2}>
              <Text variant="muted">
                {`Delete note from ${formatDateTime(
                  note.updated_at,
                )}? This removes it from the transcript.`}
              </Text>
              <Inline>
                <Button
                  disabled={isDeleting}
                  size="sm"
                  type="button"
                  variant="secondary"
                  onClick={() => {
                    setIsConfirmingDelete(false);
                  }}
                >
                  Cancel
                </Button>
                <Button
                  disabled={isDeleting}
                  size="sm"
                  type="button"
                  variant="danger"
                  onClick={() => {
                    onDeleteNote(note);
                  }}
                >
                  {isDeleting ? "Deleting..." : "Confirm delete"}
                </Button>
              </Inline>
            </Stack>
          ) : null}
        </>
      )}
    </Stack>
  );
}
